/* eslint-disable @typescript-eslint/await-thenable */
import { DSModal } from '@elliemae/ds-modal';
import { DSTab, DSTabs } from '@elliemae/ds-tabs';
import {
  useLookupOptions,
  useUserData,
  mockLoanData,
  useRawLookupOptions,
  isGovtLoan,
  EppsLoanInformation,
} from '@frontend/pricing-search';
import { cloneDeep } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  calculateGovtUpfrontAmount,
  convertPriceToParBased,
  prepareLoanDocTypes,
} from '../../common/app-helpers';
import {
  UPDATE_ADJUSTMENT_DETAILS_CACHE,
  UPDATE_LOANINFO,
  UPDATE_PROPERTY_DETAILS_CACHE,
} from '../../common/constants/ActionConstants';
import { RequestActionType } from '../../common/constants/enumTypes';
import {
  getRates,
  getWebhookRates,
  setAllRatesRowData,
} from '../../common/Qualification/QualificationActions';
import {
  getLoanCatcherAdjustments,
  processLoancatcherAdjustments,
} from '../../common/services/lockSummaryServices';
import {
  KPI,
  endInitialLoad,
  isInitialLoadComplete,
  kpiEnd,
  kpiStart,
} from '../../common/services/kpiTracking';
import Rights from '../../common/services/Rights';
import { getLoanLimits } from '../../common/services/searchFormServices';
import Session, {
  APPLICATION_NAME,
  IS_BUYSIDE,
  USER_TYPE,
} from '../../common/services/Session';
import { calcDeltas } from '../../common/utils/calculateTarget';
import createAction from '../../common/utils/createAction';
import { DataProvider } from '../../common/utils/customHooks/useAppData';
import { useIsZeroBasedPricing } from '../../common/utils/customHooks/useIsZeroBasedPricing';
import { isEmpty } from '../../common/utils/shared';
import { openGlobalErrorModalAction } from '../../data/globalErrorModal/actionCreators';
import { setRedirectTabAction } from '../../data/landing/actions';
import { setLoanLimits } from '../../data/lockSummary/actions';
import { fetchCurrentPersonaAccessRules } from '../../data/personaAccess/actionCreators';
import { closeLoader } from '../../data/screenLoader/actions';
import {
  useLoanInformationField,
  useLockedProgramSelector,
  useOriginalLoanInformationSelector,
  useOriginalLoanInfoSelector,
  usePropertyDetailsCache,
  useUserRolesSelector,
} from '../../data/useSelectors';
import { PATH_SEARCH_FOR_PRODUCTS } from '../../route/constants';
import { AppDispatch } from '../../store';
import { lookupsActions } from '../../store/lookups';
import AdjustmentDetailsView from '../AdjustmentDetails/AdjustmentDetailsView';
import {
  StyledEppsContainerBody,
  StyledEppsParentView,
  StyledEppsWrapper,
} from '../global/styles/styled';
import { AllRatesView } from './components/AllRatesView';
import { BestExGrid } from './components/BestExGrid';
import { ClassicGrid } from './components/ClassicGrid';
import { IneligibleProductsGrid } from './components/IneligiblePrograms';
import { IneligibleUnavailableModal } from './components/IneligibleUnavailableModal';
import { QuickSearch } from './components/QuickSearch';
import { applyFilter, getTarget } from './helpers';

const loanDataSelector = (res) => res?.EPPS?.loanInfo ?? mockLoanData;
const qualificationResultSelector = ({ EPPS }) =>
  (EPPS && EPPS.loanPrograms) || {};
const loanLimitsSelector = (epps) => epps?.lockSummary?.loanLimits;
let hasProcessedLoanCatcherAdjustments = false;

interface SearchResultProps {
  defaultTab?: 'Classic' | 'BestEx';
  asEmbedded?: boolean;
  navigateOnError?: (path: string) => void;
}

export const SearchResult = ({
  defaultTab,
  asEmbedded = false,
  navigateOnError,
}: SearchResultProps = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const targetValue = useLoanInformationField('target');
  const qualificationResult = useSelector(qualificationResultSelector);
  const loanLimitsResult = useSelector(loanLimitsSelector);
  const loanData = useSelector(loanDataSelector);
  const { requestAction, loanInformation } = loanData;
  const allRatesRowData = useSelector(
    (state: any) => state.EPPS.allRatesRowData,
  );
  const action = createAction(UPDATE_LOANINFO);
  const tpoPricingGroup = useSelector(
    ({ personaAccess }) => personaAccess.pricingGroup,
  );
  const [loanPrograms, setLoanPrograms] = useState([]);
  const [filters] = useState({});
  const [target, setTarget] = useState(getTarget(loanData));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshPrograms, setRefreshPrograms] = useState(false);
  const [data, setData] = useState({});
  const [user] = useState(Session.get(USER_TYPE));
  const [activeTab, setActiveTab] = useState('Classic');
  const refEligible = useRef<any>('');
  const refIneligible = useRef<any>('');
  const refBestExecution = useRef<any>('');
  const [loanLimitsData, setloanLimitsData] = useState([]);
  const [eligibleProducts, setEligibleProducts] = useState(0);
  const [ineligibleProducts, setIneligibleProducts] = useState(0);
  const [bestExecution, setBestExecution] = useState(0);
  const { focusIneligible, status } = useLockedProgramSelector();
  const userRoles = useUserRolesSelector();
  const propertyDetailsCache = usePropertyDetailsCache() || [];
  const govtUpfrontFees = useLookupOptions('fee') || [];
  const documentationTypes = useLookupOptions('documentationTypes') || [];
  const loanTermsLookups = useLookupOptions('loanTerms');
  let ltv = useOriginalLoanInformationSelector('ltv');
  const originalLoanInformation = useOriginalLoanInformationSelector();
  const originalEffectiveDate = useOriginalLoanInfoSelector(
    'relockRequest.effectiveDate',
  );
  const buysideAdjustments = useSelector(({ epps }) =>
    Object.values(epps.origin.buySideAdjustments ?? {}).flat(1),
  );
  const navigate = useNavigate();

  const isBuySide = Session.get(IS_BUYSIDE);

  const getBestExRows = !isEmpty(qualificationResult.productGroups)
    ? qualificationResult.productGroups.map((row) => ({ ...row, parent: true }))
    : [];
  let { defaultUserView = 'BestEx' } = useUserData();
  if (defaultTab) defaultUserView = defaultTab;
  const tabsToRender = useRef<any[]>([]);

  const updateAdjustmentDetailsCache = createAction(
    UPDATE_ADJUSTMENT_DETAILS_CACHE,
  );
  const updatePropertyDetailsCache = createAction(
    UPDATE_PROPERTY_DETAILS_CACHE,
  );

  const originLoanLoaded = useSelector(
    ({ EPPS }) => EPPS?.loanInfo && !EPPS?.loanInfo.isMockData,
  );
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const allLookups = useRawLookupOptions();
  // show ineligible tab for all scenarios
  // except show/hide ineligible tab when it's a relock/lock update and changeProgramOnReLock is true/false in client settings
  const isRelockORLockUpdate = useMemo(
    () =>
      requestAction === RequestActionType.ValidateReLock ||
      requestAction === RequestActionType.LockUpdate,
    [requestAction],
  );
  const hideIneligibleTab = useMemo(
    () =>
      isRelockORLockUpdate &&
      !allLookups?.clientSettings?.[0]?.encompassSettings
        ?.changeProgramOnReLock,
    [allLookups, isRelockORLockUpdate],
  );

  useEffect(() => {
    tabsToRender.current = [];
    if (Rights.accessStandardView) {
      tabsToRender.current.push({
        tabId: 'Classic',
        key: uuidv4(),
        title: 'Standard',
      });
    }
    if (Rights.accessBestexView) {
      tabsToRender.current.push({
        tabId: 'BestEx',
        key: uuidv4(),
        title: 'BestEx',
      });
    }
    tabsToRender.current.splice(1, 0, {
      tabId: 'Ineligible',
      key: uuidv4(),
      title: 'Ineligible Programs',
      style: hideIneligibleTab ? { display: 'none' } : {},
    });
    if (tabsToRender.current.length === 1 && Rights.accessStandardView) {
      defaultUserView = 'Classic';
    }
    window.scrollTo(0, 0);
  }, [hideIneligibleTab, userRoles?.roleName]);

  useEffect(() => {
    setActiveTab(defaultUserView);
  }, [defaultUserView]);

  useEffect(() => {
    setloanLimitsData(loanLimitsResult);
  }, [loanLimitsResult]);

  useEffect(() => {
    setTarget(getTarget(loanData));
  }, [loanData]);

  useEffect(() => {
    if (focusIneligible) {
      setActiveTab('Ineligible');
    }
  }, [focusIneligible]);

  // calculate deltas
  const handleCalculateDeltas = () => {
    const calculateTarget = getTarget(loanData);
    const { loanPrograms: programs } = qualificationResult;
    const cloneLoanPrograms = [...programs];
    const getDeltas = calcDeltas(
      cloneLoanPrograms,
      calculateTarget,
      isZeroBasedPricing,
    );
    setLoanPrograms(getDeltas);
  };

  useEffect(() => {
    const { loanPrograms: programs } = qualificationResult;
    if (programs) {
      handleCalculateDeltas();
    }
  }, [qualificationResult, loanData, targetValue]);

  const qualifyPrograms = async (newLoanData?: any, isSearchPage = true) => {
    setRefreshPrograms(true);
    const loan = newLoanData || loanData;
    const productType = loan?.productType || [];
    const isHeloc = loanTermsLookups
      .filter((elem) => productType.includes(elem.value))
      .some((elem) => elem.heloc);
    const isVAType = loan.standardProducts?.includes(
      EppsLoanInformation.StandardProducts.VA,
    );
    const isFHAType = loan.standardProducts?.includes(
      EppsLoanInformation.StandardProducts.FHA,
    );
    const isUSDAType = loan.standardProducts?.includes(
      EppsLoanInformation.StandardProducts.USDA,
    );
    const applicationName = Session.get(APPLICATION_NAME);

    prepareLoanDocTypes(loan, documentationTypes);

    if (isHeloc) {
      loan.loanType = 'HELOC';
      if (loan.loanInformation.lienPosition === 1) {
        loan.loanInformation.helocInitialDraw =
          loan.loanInformation.firstMortgageAmount;
      } else if (loan.loanInformation.lienPosition === 2) {
        loan.loanInformation.helocInitialDraw =
          loan.loanInformation.secondMortgageAmount;
      }
    } else if (isVAType) {
      loan.loanType = 'VA';
    } else if (isFHAType) {
      loan.loanType = 'FHA';
    } else if (isUSDAType) {
      loan.loanType = 'USDA';
    } else {
      loan.loanInformation.helocInitialDraw = null;
      // loan.loanType = 'Conventional';
    }
    if (applicationName === 'TPO') {
      loan.facadeUserID = tpoPricingGroup;
    }
    const loanLtv = loan?.loanInformation?.ltv;
    if (loanLtv) {
      ltv = loanLtv;
    }
    const { feeAmount, feeAmountFinanced, feeAmountPaidinCash, govtFeeFactor } =
      calculateGovtUpfrontAmount(
        loan,
        originalLoanInformation,
        govtUpfrontFees,
        ltv,
      );
    if (feeAmount) {
      loan.loanInformation.financedAmount = feeAmountFinanced || 0;
      loan.loanInformation.feeAmount = feeAmount || 0;
      loan.loanInformation.feeAmountFinanced = feeAmountFinanced || 0;
      loan.loanInformation.feeAmountPaidinCash = feeAmountPaidinCash || 0;
      loan.loanInformation.govtFeeFactor = govtFeeFactor || 0;
    }
    const copyLoan = cloneDeep(loan);
    if (copyLoan.lockDays != null && !Array.isArray(copyLoan.lockDays)) {
      copyLoan.lockDays = [copyLoan.lockDays];
    }
    copyLoan.loanInformation.target = convertPriceToParBased(
      isZeroBasedPricing,
      copyLoan.loanInformation.target,
      true,
      copyLoan,
    );

    if (isBuySide) {
      copyLoan.relockRequest.specificProgramID = null;
      copyLoan.relockRequest.specificRateDataID = null;
    }
    if (originalEffectiveDate && copyLoan.relockRequest?.effectiveDate) {
      const initialDate = new Date(originalEffectiveDate.toString());
      const updatedDate = new Date(copyLoan.relockRequest.effectiveDate);
      if (initialDate.getTime() !== updatedDate.getTime()) {
        copyLoan.wcpRequest = null;
      }
    }
    // First qualify runs during the initial load; later qualifies are rates-only.
    const isRequalify = isInitialLoadComplete();
    if (isRequalify) kpiStart(KPI.Qualification);
    try {
      if (user && user.userType === 'StandAlone') {
        await dispatch(getRates(copyLoan));
      } else {
        await dispatch(getWebhookRates(copyLoan, navigateOnError ?? navigate));
        if (
          propertyDetailsCache?.state !== copyLoan?.property?.state ||
          propertyDetailsCache?.county !== copyLoan?.property?.county ||
          propertyDetailsCache?.zip !== copyLoan?.property?.zip
        ) {
          // Property details have changed, query loan limits again
          const updatedPropertyDetailsCache = {
            state: loanData.property.state,
            county: loanData.property.county,
            zip: loanData.property.zip,
          };
          dispatch(updatePropertyDetailsCache(updatedPropertyDetailsCache));
          getLoanLimits(loanData).then((res) => {
            dispatch(setLoanLimits(res));
          });
        }
      }
    } catch (e) {
      if (isRequalify) kpiEnd(KPI.Qualification, { outcome: 'error' });
      else endInitialLoad({ outcome: 'error' });
      throw e;
    }
    dispatch(updateAdjustmentDetailsCache([]));

    const loanCatcherAdjustments = await getLoanCatcherAdjustments(
      loan.loanInformation,
    );
    // Process LoanCatcher adjustments only once per search results page load
    if (
      loanCatcherAdjustments?.length > 0 &&
      !hasProcessedLoanCatcherAdjustments &&
      // Don't reprocess for Buy/Sell requests, those are already done on Lock Summary screen
      loan.requestAction !== RequestActionType.BuyRequest &&
      loan.requestAction !== RequestActionType.SellRequest
    ) {
      processLoancatcherAdjustments(
        dispatch,
        loan,
        buysideAdjustments,
        loanCatcherAdjustments,
      );
      hasProcessedLoanCatcherAdjustments = true;
    }

    copyLoan.loanInformation.target = convertPriceToParBased(
      isZeroBasedPricing,
      copyLoan.loanInformation.target,
      false,
      copyLoan,
    );
    copyLoan.isZeroBasedPricing = isZeroBasedPricing;

    dispatch(action(copyLoan));
    dispatch(fetchCurrentPersonaAccessRules() as any);
    setRefreshPrograms(false);
    if (isRequalify) kpiEnd(KPI.Qualification);
    else endInitialLoad();
  };

  useEffect(() => {
    if (allRatesRowData) {
      const refreshedProgram = qualificationResult.loanPrograms.find(
        ({ programID }) => programID === allRatesRowData.programID,
      );
      dispatch(setAllRatesRowData(refreshedProgram));
    }
  }, [qualificationResult.loanPrograms]);

  useEffect(() => {
    if (!originLoanLoaded) {
      return;
    }

    const getData = async () => {
      const { loanPurpose } = loanData.loanInformation;
      if (loanPurpose === 99) {
        dispatch(
          openGlobalErrorModalAction({
            errorMessage: 'Loan Purpose of Other is not supported.',
            onCloseCallback: () => {
              dispatch(setRedirectTabAction(PATH_SEARCH_FOR_PRODUCTS));
              dispatch(
                lookupsActions.getLookupsDataAction({ dispatch } as any),
              );
            },
          }) as any,
        );
      } else {
        await Promise.all([
          qualifyPrograms(),
          dispatch(lookupsActions.getLookupsDataAction({ dispatch } as any)),
        ]);
      }
      dispatch(closeLoader());
      endInitialLoad();
    };
    getData();
    setloanLimitsData(loanLimitsResult);
  }, [originLoanLoaded]);

  useEffect(() => {
    setTarget(getTarget(loanData));
  }, [loanData]);

  const showDetailsModal = (
    e?: any,
    idLoan?: any,
    programId?: any,
    rateDataId?: any,
    lienPosition?: any,
  ) => {
    e.preventDefault();
    setData({
      loanId: idLoan,
      lienPosition,
      rateDataId,
      programId,
      requestAction,
      loanChannel: loanInformation?.loanChannel,
    });
    setIsModalOpen(!isModalOpen);
  };

  const ProgramDetailsModal = () => (
    <DSModal
      isOpen={isModalOpen}
      modalTitle="Program Details"
      onClose={(e) => showDetailsModal(e, null, null)}
      showFooter={false}
    >
      <p>
        <AdjustmentDetailsView data={data} />
      </p>
    </DSModal>
  );

  if (allRatesRowData) {
    return (
      <AllRatesView
        allRatesData={allRatesRowData}
        loanData={loanData}
        qualifyPrograms={qualifyPrograms}
      />
    );
  }

  const body = (
    <StyledEppsContainerBody className="search-result">
      <StyledEppsWrapper paddingBottom="0">
        {!isBuySide && (
          <div style={{ paddingTop: '16px' }}>
            <QuickSearch onSearch={qualifyPrograms} />
          </div>
        )}
        {isBuySide && loanPrograms.length !== 0 && status !== 'eligible' && (
          <IneligibleUnavailableModal />
        )}
        <DataProvider
          value={{
            refEligible,
            refIneligible,
            refBestExecution,
            setEligibleProducts,
            setIneligibleProducts,
            setBestExecution,
          }}
        >
          {!isEmpty(Rights) && !isEmpty(tabsToRender.current) && (
            <DSTabs
              animated={false}
              height="100%"
              activeTab={activeTab}
              onTabChange={setActiveTab}
            >
              {tabsToRender.current.map(({ tabId, key, title, style }) => {
                const counters = {
                  Classic: eligibleProducts,
                  Ineligible: ineligibleProducts,
                  BestEx: bestExecution,
                };
                let componentToRender: any = null;
                if (tabId === 'Classic') {
                  componentToRender = () => (
                    <ClassicGrid
                      loanPrograms={loanPrograms}
                      refreshPrograms={refreshPrograms}
                      onSearch={qualifyPrograms}
                      applyFilter={() => {
                        refEligible.current = false;
                        refIneligible.current = false;
                        refBestExecution.current = false;
                        return applyFilter(filters);
                      }}
                      updateAdjustmentDetailsCache={
                        updateAdjustmentDetailsCache
                      }
                    />
                  );
                }
                if (tabId === 'BestEx') {
                  componentToRender = () => (
                    <BestExGrid
                      productGroups={getBestExRows}
                      loanPrograms={loanPrograms}
                      target={target}
                      ratesBestEx={qualificationResult.ratesBestEx}
                      lockDaysBestEx={qualificationResult.lockDays}
                      onSearch={qualifyPrograms}
                      updateAdjustmentDetailsCache={
                        updateAdjustmentDetailsCache
                      }
                    />
                  );
                }
                if (tabId === 'Ineligible') {
                  componentToRender = () => (
                    <IneligibleProductsGrid
                      loanPrograms={loanPrograms}
                      loanLimits={loanLimitsData}
                      refreshPrograms={refreshPrograms}
                    />
                  );
                }
                return (
                  <DSTab
                    tabId={tabId}
                    title={`${title} (${counters[tabId]})`}
                    key={key}
                    style={style}
                  >
                    {componentToRender()}
                  </DSTab>
                );
              })}
            </DSTabs>
          )}
        </DataProvider>
        <ProgramDetailsModal />
      </StyledEppsWrapper>
    </StyledEppsContainerBody>
  );

  if (asEmbedded) return body;

  return <StyledEppsParentView overflow="hidden">{body}</StyledEppsParentView>;
};
